/**
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 */

/**
 * The code in this file is based on https://github.com/WICG/focus-visible
 * with some modifications made to change the focus-visible behaviour for inputs
 * with type text, number, email etc.
 * Also typed for TypeScript.
 */

// Keeps track of whether this polyfill has already been loaded from somewhere else

// Provides explicit indication of elements receiving focus through keyboard interaction rather than mouse or touch.
export var applyFocusVisiblePolyfill = function applyFocusVisiblePolyfill() {
  var doc = typeof window === "undefined" ? undefined : document;
  // In case file is imported in SSR context or has already run, don't polyfill anything
  if (!doc || window.focusVisibleApplied) {
    return function () {};
  }
  window.focusVisibleApplied = true;
  var NAVIGATION_KEYS = ["Tab", "ArrowUp", "ArrowRight", "ArrowDown", "ArrowLeft", "Home", "End", "PageUp", "PageDown", "Enter", " ", "Escape", /* IE9 and Firefox < 37 */
  "Up", "Right", "Down", "Left", "Esc"];
  var FOCUS_CLASS_NAME = "focus-visible";
  var keyboardFocus = false;
  var elements = doc.getElementsByClassName(FOCUS_CLASS_NAME);
  var onKeydownHandler = function onKeydownHandler(event) {
    if (event.ctrlKey || event.altKey || event.metaKey || NAVIGATION_KEYS.indexOf(event.key) === -1) {
      return;
    }
    keyboardFocus = true;
    if (doc && doc.activeElement && doc.activeElement !== doc.body && doc.activeElement.tagName !== "TEXTAREA" && doc.activeElement.tagName !== "INPUT") {
      doc.activeElement.classList.add(FOCUS_CLASS_NAME);
    }
  };
  var onMousedownHandler = function onMousedownHandler() {
    keyboardFocus = false;
    for (var i = 0; i < elements.length; i++) {
      elements[i].classList.remove(FOCUS_CLASS_NAME);
    }
  };
  var onFocusHandler = function onFocusHandler(event) {
    var classList = event.target.classList;
    if (classList && keyboardFocus) {
      classList.add(FOCUS_CLASS_NAME);
    }
  };
  var onBlurHandler = function onBlurHandler(event) {
    var classList = event.target.classList;
    if (classList) {
      classList.remove(FOCUS_CLASS_NAME);
    }
  };
  doc.addEventListener("keydown", onKeydownHandler, true);
  doc.addEventListener("mousedown", onMousedownHandler, true);
  doc.addEventListener("focus", onFocusHandler, true);
  doc.addEventListener("blur", onBlurHandler, true);

  // Return a clean up function
  return function () {
    doc.removeEventListener("keydown", onKeydownHandler);
    doc.removeEventListener("mousedown", onMousedownHandler);
    doc.removeEventListener("focus", onFocusHandler);
    doc.removeEventListener("blur", onBlurHandler);
  };
};